import { useState } from "react";
import { Grid } from "@mui/material";
import MDBox from "components/MDBox";

import { useQuery } from "@tanstack/react-query";

import { TitleButton } from "ui-components/Form";
import { Fieldset } from "ui-components/Form";
import { FormTitle, DatePickerField, Text, SelectField } from "ui-components/Form";

import { getDefShipZone } from "services/data.outbound.service";
import { getConsigneeOptions2 } from "services/data.internal.service";
import { countryOptions } from "layouts/views/common/options";

function DO({ headerRender, customerCode, control, errors, setValue, getValues, mode = "ADD" }) {
  const [consigneeOptions, setConsigneeOptions] = useState([]);

  useQuery({
    queryKey: ["consignee-options", { customerCode }],
    queryFn: () => getConsigneeOptions2({ customerCode }),
    enabled: customerCode !== null,
    onSuccess: (data) => {
      setConsigneeOptions(
        data.data.data.map((x) => {
          const { consigneeCode, ...consignee } = x;
          return {
            label: consigneeCode,
            value: consigneeCode,
            ...consignee,
          };
        })
      );
    },
  });

  const [postcode, setPostcode] = useState(null);
  const { shipPostcode } = getValues;
  useQuery({
    queryKey: ["carrier", { customerCode, postcode }],
    queryFn: () => getDefShipZone({ customerCode, postcode }),
    enabled: customerCode !== null && postcode !== null,
    onSuccess: (data) => {
      console.log(data.data.carrierCode);
      setValue("carrier", data.data.carrierCode);
      setValue("shippingZone", data.data.shippingZone);
    },
  });

  const onShipPostcodeChange = (e) => {
    const { value } = e.target;
    setPostcode(value);
    setValue("shipPostcode", value);
  };

  // useQuery({
  //   queryKey: ["orderType", { customerCode }],
  //   queryFn: () => getOrderType({ customerCode }),
  //   enabled: customerCode !== null,
  //   onSuccess: (data) => {
  //     var orderType = data.data;
  //     console.log(orderType);
  //     setValue("orderType", orderType);
  //     if (orderType === "B2B") setValue("shipmentType", "NonIntegrated");
  //     else setValue("shipmentType", "Integrated");
  //   },
  // });

  const onConsigneeChange = (e, option) => {
    if (option === null) {
      setValue("consigneeCode", option);
      return;
    }
    const {
      value,
      consigneeName,
      shipAddress1,
      shipAddress2,
      shipPostcode,
      shipCity,
      shipState,
      shipCountry,
      shipPhoneNo,
      billAddress1,
      billAddress2,
      billPostcode,
      billCity,
      billState,
      billCountry,
      billPhoneNo,
      contactEmail,
      contactPerson,
    } = option;
    setValue("consigneeCode", value);
    setValue("customerName", consigneeName);
    setValue("shipCompany", consigneeName);
    setValue("shipAddress1", shipAddress1);
    setValue("shipAddress2", shipAddress2);
    setValue("shipPostcode", shipPostcode);
    setValue("shipCity", shipCity);
    setValue("shipState", shipState);
    setValue("shipCountry", shipCountry);
    setValue("shipTelNo", shipPhoneNo);
    setValue("billCompany", consigneeName);
    setValue("billAddress1", billAddress1);
    setValue("billAddress2", billAddress2);
    setValue("billPostcode", billPostcode);
    setValue("billCity", billCity);
    setValue("billState", billState);
    setValue("billCountry", billCountry);
    setValue("billTelNo", billPhoneNo);
    setValue("shipContactPerson", contactPerson);
    setValue("billContactPerson", contactPerson);
    setValue("shipEmail", contactEmail);
    setValue("billEmail", contactEmail);

    setPostcode(shipPostcode);
  };

  const applyBilling = () => {
    const {
      shipCompany,
      shipContact,
      shipAddress1,
      shipAddress2,
      shipPostcode,
      shipCity,
      shipState,
      shipCountry,
      shipPhone,
      shipEmail,
    } = getValues();
    setValue("billCompany", shipCompany);
    setValue("billContact", shipContact);
    setValue("billAddress1", shipAddress1);
    setValue("billAddress2", shipAddress2);
    setValue("billPostcode", shipPostcode);
    setValue("billCity", shipCity);
    setValue("billState", shipState);
    setValue("billCountry", shipCountry);
    setValue("billPhone", shipPhone);
    setValue("billEmail", shipEmail);
  };

  const onDOTypeChange = (e, option) => {
    setValue("doType", option);
    if (option === "B2B") setValue("shipmentType", "NonIntegrated");
    else setValue("shipmentType", "Integrated");
  };

  function renderFn(property, index, length) {
    const { size, disabled, editable, ...data } = property;
    const xs = 6 / 2;
    const fieldProps = {
      ...data,
      control,
      errors,
      ...(disabled && { disabled }),
      ...(mode === "EDIT" && editable === false && { disabled: true, type: "text" }),
      ...(data.name === "shipPostcode" && { onChange: onShipPostcodeChange }),
    };
    let renderField;
    switch (fieldProps.type) {
      case "text":
      case "number":
        renderField = <Text field={fieldProps} />;
        break;
      case "select":
        let options = [];
        let onChange = null;
        switch (data.name) {
          case "consigneeCode":
            options = consigneeOptions;
            onChange = onConsigneeChange;
            break;
          case "shipCountry":
          case "billCountry":
            options = countryOptions;
            break;
        }
        renderField = <SelectField field={fieldProps} options={options} onChange={onChange} />;
        break;
      case "date":
        renderField = <DatePickerField field={fieldProps} />;
        break;
      case "button":
        let button = null;
        button = <TitleButton label="Apply Shipping to Billing" onClick={applyBilling} />;
        renderField = button;
        break;
    }
    return (
      <Grid key={index} item xs={xs}>
        {renderField}
      </Grid>
    );
  }

  return (
    <MDBox>
      <FormTitle title={mode === "EDIT" ? "Edit Delivery Order" : "Add Delivery Order"} />
      <MDBox mt={1.625}>
        {headerRender.map((rows, i) => {
          if (rows[0].type === "fieldset") {
            return (
              <Fieldset label={rows[0].label} key={i}>
                {rows[0].fieldsetRender.map((fs_rows, fs_i) => (
                  <Grid container spacing={3} key={fs_i}>
                    {fs_rows.map((fs_row, fs_index) => renderFn(fs_row, fs_index, fs_rows.length))}
                  </Grid>
                ))}
              </Fieldset>
            );
          }
          return (
            <Grid container spacing={3} key={i}>
              {rows.map((row, index) => renderFn(row, index, rows.length))}
            </Grid>
          );
        })}
      </MDBox>
    </MDBox>
  );
}

export default DO;
